/**
 * VehicleTimeline — Linha do tempo animada do veículo dentro da oficina.
 *
 * Renderiza os marcos ("Recebido", "Em Inspeção", "Peças"...) numa linha horizontal (desktop)
 * ou vertical (mobile), com um carrinho SVG que anima do primeiro marco até o marco atual em loop.
 *
 * Props:
 *  - timeline: { steps: [{id, label, done, current, position}], current_position, total }
 *  - status_label: rótulo textual do status atual (para acessibilidade e destaque)
 */
import { useEffect, useRef, useState } from "react";
import { Car, CheckCircle2, Circle } from "lucide-react";

export default function VehicleTimeline({ timeline, status_label }) {
  const containerRef = useRef(null);
  const [dotPositions, setDotPositions] = useState([]);
  const [animKey, setAnimKey] = useState(0);

  const steps = timeline?.steps || [];
  const currentPos = timeline?.current_position ?? 0;

  // Calcula posições X reais dos pontos para animar o carrinho de forma responsiva
  useEffect(() => {
    if (!containerRef.current) return;
    const measure = () => {
      const track = containerRef.current;
      if (!track) return;
      const trackRect = track.getBoundingClientRect();
      const dots = track.querySelectorAll("[data-dot]");
      const positions = Array.from(dots).map((d) => {
        const r = d.getBoundingClientRect();
        return r.left - trackRect.left + r.width / 2;
      });
      setDotPositions(positions);
    };
    measure();
    // Re-medir em resize
    const ro = new ResizeObserver(measure);
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [steps.length]);

  // Reset animation loop key when timeline changes
  useEffect(() => {
    setAnimKey((k) => k + 1);
  }, [currentPos, steps.length]);

  if (!steps.length) return null;

  const startX = dotPositions[0] || 0;
  const endX = dotPositions[currentPos] || startX;

  return (
    <div className="w-full" data-testid="vehicle-timeline">
      {/* ---------- DESKTOP / TABLET (horizontal) ---------- */}
      <div className="hidden sm:block">
        <div
          ref={containerRef}
          className="relative pt-9 pb-6 px-2"
          style={{ minHeight: 100 }}
        >
          {/* Track background */}
          <div className="absolute top-[52px] left-4 right-4 h-1 bg-secondary rounded-full" />

          {/* Track filled up to current */}
          {dotPositions.length > 0 && (
            <div
              className="absolute top-[52px] h-1 bg-primary rounded-full transition-all duration-700"
              style={{
                left: startX,
                width: Math.max(0, endX - startX),
              }}
            />
          )}

          {/* Cart animado — só aparece após medir posições */}
          {dotPositions.length > 0 && endX > startX && (
            <div
              key={animKey}
              className="absolute top-[38px] -translate-x-1/2 z-10 pointer-events-none aab-cart-anim"
              style={{
                "--cart-start": `${startX}px`,
                "--cart-end": `${endX}px`,
              }}
              aria-hidden="true"
            >
              <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg ring-4 ring-background">
                <Car className="w-4 h-4" />
              </div>
            </div>
          )}
          {/* Se veículo está no marco 0, só mostra o carrinho parado ali */}
          {dotPositions.length > 0 && endX === startX && (
            <div
              className="absolute top-[38px] -translate-x-1/2 z-10 pointer-events-none animate-pulse"
              style={{ left: startX }}
              aria-hidden="true"
            >
              <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg ring-4 ring-background">
                <Car className="w-4 h-4" />
              </div>
            </div>
          )}

          {/* Dots + labels */}
          <div className="relative flex justify-between">
            {steps.map((s, i) => (
              <div
                key={s.id}
                data-dot
                data-testid={`timeline-step-${s.id}`}
                className="flex flex-col items-center relative z-[1]"
                style={{ flex: "1 1 0" }}
              >
                <div
                  className={`w-4 h-4 rounded-full border-2 transition-all ${
                    s.current
                      ? "bg-primary border-primary ring-4 ring-primary/20 scale-125"
                      : s.done
                      ? "bg-primary border-primary"
                      : "bg-background border-border"
                  }`}
                />
                <div
                  className={`mt-2.5 text-[10px] uppercase tracking-widest text-center leading-tight px-0.5 ${
                    s.current
                      ? "text-primary font-bold"
                      : s.done
                      ? "text-foreground/80"
                      : "text-muted-foreground"
                  }`}
                  style={{ maxWidth: 90 }}
                >
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ---------- MOBILE (vertical) ---------- */}
      <div className="sm:hidden">
        <div className="relative pl-6 pt-2">
          {/* Track */}
          <div className="absolute left-[9px] top-2 bottom-2 w-0.5 bg-secondary rounded-full" />
          {steps.length > 1 && (
            <div
              className="absolute left-[9px] top-2 w-0.5 bg-primary rounded-full transition-all duration-700"
              style={{ height: `${(currentPos / (steps.length - 1)) * 100}%` }}
            />
          )}

          {steps.map((s) => (
            <div key={s.id} className="flex items-start gap-3 py-2 relative" data-testid={`timeline-step-mobile-${s.id}`}>
              <div className="absolute left-[-16px]">
                {s.done ? (
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center ${
                      s.current ? "bg-primary ring-4 ring-primary/20" : "bg-primary"
                    }`}
                  >
                    {s.current ? <Car className="w-3 h-3 text-primary-foreground" /> : <CheckCircle2 className="w-3 h-3 text-primary-foreground" />}
                  </div>
                ) : (
                  <div className="w-5 h-5 rounded-full bg-background border-2 border-border flex items-center justify-center">
                    <Circle className="w-2 h-2 text-muted-foreground" />
                  </div>
                )}
              </div>
              <div
                className={`text-sm leading-tight ${
                  s.current ? "font-bold text-primary" : s.done ? "text-foreground" : "text-muted-foreground"
                }`}
              >
                {s.label}
                {s.current && (
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground/80 font-normal mt-0.5">
                    Etapa atual
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {status_label && (
        <div className="mt-1 sm:mt-3 text-center text-[11px] uppercase tracking-[0.2em] text-muted-foreground sr-only">
          Status atual: {status_label}
        </div>
      )}
    </div>
  );
}
